import { useEffect, useState } from "react";
import axios from "axios";
import { Users, CalendarCheck, Mail, TrendingUp, FileText, Compass, DollarSign } from "lucide-react";
import AdminLogin from "@/components/admin/AdminLogin";
import {
  UpcomingBookingsTable,
  LeadsTable,
  PaymentsTable,
  InstalmentPlansTable,
  ReferralsTable,
} from "@/components/admin/AdminTables";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-bone/10 bg-forest/40 p-8" data-testid={testId}>
    <Icon size={22} className="mb-5 text-terra" />
    <p className="font-serif text-4xl font-semibold text-bone">{value}</p>
    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">{label}</p>
  </div>
);

function useAdminStats(key, onAuthError) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!key) return;
    axios
      .get(`${API}/admin/stats`, { headers: { "X-Admin-Key": key } })
      .then((res) => {
        setStats(res.data);
        sessionStorage.setItem("bc_admin_key", key);
      })
      .catch(() => {
        setStats(null);
        sessionStorage.removeItem("bc_admin_key");
        onAuthError();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return stats;
}

export default function AdminDashboard() {
  const [key, setKey] = useState(sessionStorage.getItem("bc_admin_key") || "");
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const stats = useAdminStats(key, () => {
    setError("Invalid access key.");
    setKey("");
  });

  if (!key || !stats) {
    return (
      <AdminLogin
        input={input}
        setInput={setInput}
        error={error}
        onSubmit={(e) => {
          e.preventDefault();
          setError("");
          setKey(input.trim());
        }}
      />
    );
  }

  const maxDaily = Math.max(1, ...stats.daily_visits.map((d) => d.count));
  const maxService = Math.max(1, ...stats.service_counts.map((s) => s.count));

  return (
    <div className="min-h-screen bg-pine px-6 py-16 md:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.35em] text-terra">BookCare LLC · Owner Analytics</p>
        <h1 className="mb-12 font-serif text-4xl font-medium text-bone">How the practice is <span className="italic text-terra">performing</span>.</h1>

        <div className="grid grid-cols-1 gap-px border border-bone/10 bg-bone/10 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard icon={Users} label="Site visits (all time)" value={stats.total_visits} testId="stat-visits" />
          <StatCard icon={Compass} label={`Accelerator views (${stats.accelerator_visits_30d} in 30 days)`} value={stats.accelerator_visits} testId="stat-accelerator-views" />
          <StatCard icon={CalendarCheck} label="Consultations booked" value={stats.total_bookings} testId="stat-bookings" />
          <StatCard icon={Mail} label="Contact inquiries" value={stats.total_inquiries} testId="stat-inquiries" />
          <StatCard icon={FileText} label="Checklist leads" value={stats.total_leads} testId="stat-leads" />
          <StatCard icon={TrendingUp} label="Visit → booking rate" value={`${stats.conversion_rate}%`} testId="stat-conversion" />
          <StatCard
            icon={DollarSign}
            label={`Paid online (${stats.payments_count || 0} payments)`}
            value={`$${Number(stats.total_revenue || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
            testId="stat-revenue"
          />
        </div>

        <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div className="border border-bone/10 bg-forest/30 p-8" data-testid="service-interest-chart">
            <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">Service interest (bookings + inquiries)</p>
            {stats.service_counts.length === 0 && <p className="text-sm text-bone/50">No data yet.</p>}
            <div className="flex flex-col gap-5">
              {stats.service_counts.map((s) => (
                <div key={s.service}>
                  <div className="mb-2 flex items-baseline justify-between gap-4">
                    <span className="text-sm text-bone/80">{s.service}</span>
                    <span className="font-mono text-xs text-terra">{s.count}</span>
                  </div>
                  <div className="h-2 w-full bg-bone/10">
                    <div className="h-2 bg-terra" style={{ width: `${(s.count / maxService) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-bone/10 bg-forest/30 p-8" data-testid="tier-popularity-chart">
            <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">Most popular pricing packages</p>
            {(!stats.tier_counts || stats.tier_counts.length === 0) ? (
              <p className="text-sm text-bone/50">No package selections yet — this fills in as visitors book from the pricing section.</p>
            ) : (
              <div className="flex flex-col gap-5">
                {stats.tier_counts.map((t, i) => {
                  const maxTier = Math.max(1, ...stats.tier_counts.map((x) => x.count));
                  return (
                    <div key={t.tier} data-testid={`tier-count-${i}`}>
                      <div className="mb-2 flex items-baseline justify-between gap-4">
                        <span className="text-sm text-bone/80">
                          {i === 0 && <span className="mr-2 border border-terra px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-terra">Top pick</span>}
                          {t.tier}
                        </span>
                        <span className="font-mono text-xs text-terra">{t.count}</span>
                      </div>
                      <div className="h-2 w-full bg-bone/10">
                        <div className="h-2 bg-terra" style={{ width: `${(t.count / maxTier) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border border-bone/10 bg-forest/30 p-8" data-testid="daily-visits-chart">
            <p className="mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">Visitors — last 30 days</p>
            <div className="flex h-40 items-end gap-[3px]">
              {stats.daily_visits.map((d) => (
                <div
                  key={d.date}
                  title={`${d.date}: ${d.count}`}
                  className="flex-1 bg-bone/20 transition-colors hover:bg-terra"
                  style={{ height: `${Math.max(4, (d.count / maxDaily) * 100)}%` }}
                />
              ))}
            </div>
            <div className="mt-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.15em] text-bone/40">
              <span>{stats.daily_visits[0]?.date}</span>
              <span>Today</span>
            </div>
          </div>
        </div>

        <UpcomingBookingsTable bookings={stats.upcoming_bookings} />
        <LeadsTable leads={stats.recent_leads} />
        <PaymentsTable payments={stats.recent_payments} />
        <InstalmentPlansTable plans={stats.instalment_plans} />
        <ReferralsTable referrals={stats.referrals} totalCodes={stats.total_referral_codes} />

        <a href="/" className="mt-12 inline-block font-mono text-xs uppercase tracking-[0.2em] text-bone/50 underline-offset-4 hover:text-bone hover:underline" data-testid="admin-back-link">
          ← Back to website
        </a>
      </div>
    </div>
  );
}
